import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { cn } from '../../lib/utils';
import { Button } from '../ui/button';
import { ScrollArea } from '../ui/scroll-area';
import {
  X,
  LayoutDashboard,
  Users,
  FileText,
  UserCheck,
  DollarSign,
  Building2,
  BarChart3,
  Settings,
  ChevronDown,
  ChevronRight,
  Target,
  ClipboardList,
  Calendar,
  User,
  Wallet,
  CreditCard,
  Upload,
  Bell,
  TrendingUp,
  Clock,
  CheckCircle2,
  XCircle,
  AlertCircle,
  Briefcase,
  Home,
  Phone,
  Info,
  Shield,
  FileCheck,
  Globe,
  Layers,
  Palette,
} from 'lucide-react';

// Admin sidebar menu
const adminMenu = [
  {
    title: 'Dashboard',
    icon: LayoutDashboard,
    path: '/admin/dashboard',
  },
  {
    title: 'Leads Management',
    icon: Users,
    submenu: [
      { title: 'All Leads', path: '/admin/leads' },
      // { title: 'Follow-Up Leads', path: '/admin/leads/followup' },
      { title: 'Converted Leads', path: '/admin/leads/converted' },
      { title: 'Pending Leads', path: '/admin/leads/pending' },
      { title: 'Rejected Leads', path: '/admin/leads/rejected' },
    ],
  },
  {
    title: 'Follow-Ups',
    icon: Calendar,
    submenu: [
      { title: "Today's", path: '/admin/followups/today' },
      { title: 'Upcoming', path: '/admin/followups/upcoming' },
      { title: 'Overdue', path: '/admin/followups/overdue' },
      { title: 'Completed', path: '/admin/followups/completed' },
    ],
  },
  {
    title: 'Loan / File Login',
    icon: FileText,
    submenu: [
      { title: 'Total File Login', path: '/admin/loans' },
      { title: 'Under Processing', path: '/admin/loans/processing' },
      { title: 'Approved', path: '/admin/loans/approved' },
      { title: 'Disbursed', path: '/admin/loans/disbursed' },
      { title: 'Rejected', path: '/admin/loans/rejected' },
      { title: 'Hold Cases', path: '/admin/loans/hold' },
    ],
  },
  {
    title: 'Agents Management',
    icon: UserCheck,
    submenu: [
      { title: 'All Agents', path: '/admin/agents' },
      // { title: 'Performance', path: '/admin/agents/performance' },
      // { title: 'Target Setup', path: '/admin/agents/targets' },
      // { title: "Agent Request", path: "/admin/agents/request" }
    ],
  },
  {
    title: 'Employees Management',
    icon: Briefcase,
    submenu: [
      { title: 'All Employees', path: '/admin/employees' },
      { title: 'Role & Permission', path: '/admin/employees/roles' },
      // { title: 'Activity Logs', path: '/admin/employees/logs' },
    ],
  },
  {
    title: 'Pricing Plans',
    icon: CreditCard,
    path: '/admin/plans',
  },
  {
    title: 'White Label',
    icon: Globe,
    submenu: [
      { title: 'All White Labels', path: '/admin/whitelabels' },
      { title: 'Add White Label', path: '/admin/whitelabels/add' },
      // { title: 'Feature Permissions', path: '/admin/whitelabels/permissions' },
      // { title: 'Commission Plans', path: '/admin/whitelabels/commission-plans' },
      // { title: 'Suspended WLs', path: '/admin/whitelabels/suspended' },
    ],
  },
  {
    title: 'Commission Management',
    icon: DollarSign,
    submenu: [
      { title: 'Commission Packages', path: '/admin/commission/slabs' },
      { title: 'Pending Payouts', path: '/admin/commission/pending' },
      { title: 'Approved Payouts', path: '/admin/commission/approved' },
      { title: 'Commission Ledger', path: '/admin/commission/ledger' },
    ],
  },
  {
    title: 'Banks / NBFC',
    icon: Building2,
    submenu: [
      { title: 'Loan Schemes', path: '/admin/banks' },
      // { title: 'Interest Setup', path: '/admin/banks/interest' },
      // { title: 'Processing Fees', path: '/admin/banks/fees' },
      // { title: 'Integration Settings', path: '/admin/banks/integration' },
    ],
  },
  {
    title: 'Reports',
    icon: BarChart3,
    submenu: [
      { title: 'Revenue Report', path: '/admin/reports/revenue' },
      // { title: 'Conversion Report', path: '/admin/reports/conversion' },
      { title: 'Agent-wise Report', path: '/admin/reports/agents' },
      { title: 'Bank-wise Report', path: '/admin/reports/banks' },
    ],
  },
  // {
  //   title: 'Alerts',
  //   icon: Bell,
  //   submenu: [
  //     { title: 'Pending Approvals', path: '/admin/alerts/pending' },
  //     { title: 'Overdue Files', path: '/admin/alerts/overdue' },
  //     { title: 'High Value Alerts', path: '/admin/alerts/high-value' },
  //   ],
  // },
  // {
  //   title: 'System Settings',
  //   icon: Settings,
  //   submenu: [
  //     { title: 'Company Profile', path: '/admin/settings/company' },
  //     { title: 'Role Permission', path: '/admin/settings/permissions' },
  //     { title: 'Audit Logs', path: '/admin/settings/audit' },
  //     { title: 'Security Settings', path: '/admin/settings/security' },
  //   ],
  // },
];

// Employee sidebar menu — each item declares which permissions unlock it.
// 'requiredPermissions: []' means always visible (Dashboard, Profile).
const employeeMenu = [
  {
    title: 'Dashboard',
    icon: LayoutDashboard,
    path: '/employee/dashboard',
    requiredPermissions: [], // always show
  },
  {
    title: 'Leads',
    icon: Users,
    requiredPermissions: ['leads.view', 'leads.edit'],
    submenu: [
      { title: 'All Assigned Leads', path: '/employee/leads' },
      // { title: 'Follow-up Leads', path: '/employee/leads/followup' },
      { title: 'Rejected Leads', path: '/employee/leads/rejected' },
    ],
  },
  {
    title: 'Agents',
    icon: UserCheck,
    requiredPermissions: ['agents.view', 'agents.edit'],
    submenu: [
      { title: 'All Agents', path: '/employee/agents' },
      // { title: 'Performance', path: '/employee/agents/performance' },
      // { title: 'Target Setup', path: '/employee/agents/targets' },
    ],
  },
  {
    title: 'My Commission',
    icon: Wallet,
    path: '/employee/commission',
    requiredPermissions: [],
  },
  // {
  //   title: 'Employees Management',
  //   icon: Briefcase,
  //   requiredPermissions: ['employees.view'],
  //   submenu: [
  //     { title: 'All Employees', path: '/employee/employees' },
  //     { title: 'Activity Logs', path: '/employee/employees/logs' },
  //   ],
  // },
  {
    title: 'Loan Processing',
    icon: FileText,
    requiredPermissions: ['loans.view', 'loans.edit'],
    submenu: [
      { title: 'Assigned Files', path: '/employee/loans' },
      { title: 'Under Process', path: '/employee/loans/processing' },
      { title: 'Approved', path: '/employee/loans/approved' },
      { title: 'Disbursed', path: '/employee/loans/disbursed' },
      { title: 'Rejected', path: '/employee/loans/rejected' },
    ],
  },
  // {
  //   title: 'Documents',
  //   icon: FileCheck,
  //   requiredPermissions: ['documents.view', 'documents.verify'],
  //   submenu: [
  //     { title: 'KYC Pending', path: '/employee/documents/kyc' },
  //     { title: 'Income Pending', path: '/employee/documents/income' },
  //     { title: 'Rejected Docs', path: '/employee/documents/rejected' },
  //     { title: 'Verified Docs', path: '/employee/documents/verified' },
  //   ],
  // },
  {
    title: 'Follow-Ups',
    icon: Calendar,
    requiredPermissions: ['leads.view', 'leads.edit', 'loans.view'],
    submenu: [
      { title: "Today's", path: '/employee/followups/today' },
      { title: 'Upcoming', path: '/employee/followups/upcoming' },
      { title: 'Overdue', path: '/employee/followups/overdue' },
      { title: 'Completed', path: '/employee/followups/completed' },
    ],
  },
  // {
  //   title: 'Reports',
  //   icon: BarChart3,
  //   requiredPermissions: ['reports.view', 'reports.export'],
  //   submenu: [
  //     { title: 'My Performance', path: '/employee/reports/performance' },
  //     { title: 'Assigned File Report', path: '/employee/reports/files' },
  //   ],
  // },
  // {
  //   title: 'Alerts',
  //   icon: Bell,
  //   requiredPermissions: ['leads.view', 'loans.view', 'documents.view'],
  //   submenu: [
  //     { title: 'Overdue Files', path: '/employee/alerts/overdue' },
  //     { title: 'Missing Documents', path: '/employee/alerts/missing-documents' },
  //   ],
  // },
  // {
  //   title: 'Profile',
  //   icon: User,
  //   requiredPermissions: [], // always show
  //   submenu: [
  //     { title: 'My Profile', path: '/employee/profile' },
  //     { title: 'Change Password', path: '/employee/profile/password' },
  //   ],
  // },
];

/**
 * Returns only the employee menu items the user has permission to see.
 * @param {string[]} userPermissions - array from user.permissions (login response)
 */
const filterEmployeeMenu = (userPermissions = []) => {
  const perms = new Set(userPermissions);
  return employeeMenu.filter((item) => {
    // Empty requiredPermissions = always visible
    if (!item.requiredPermissions || item.requiredPermissions.length === 0) return true;
    // Show if user has at least one of the required permissions
    return item.requiredPermissions.some((p) => perms.has(p));
  });
};

// Agent sidebar menu
const agentMenu = [
  {
    title: 'Dashboard',
    icon: LayoutDashboard,
    path: '/agent/dashboard'

  },
  {
    title: 'My Leads',
    icon: Users,
    submenu: [
      { title: 'All Leads', path: '/agent/leads' },
      // { title: 'Follow-up', path: '/agent/leads/followup' },
      { title: 'Pending', path: '/agent/leads/pending' },
      { title: 'Converted', path: '/agent/leads/converted' },
      { title: 'Rejected', path: '/agent/leads/rejected' },
    ],
  },
  {
    title: 'Loan Applications',
    icon: FileText,
    submenu: [
      { title: 'File Logged', path: '/agent/loans' },
      { title: 'Under Process', path: '/agent/loans/processing' },
      { title: 'Approved', path: '/agent/loans/approved' },
      { title: 'Disbursed', path: '/agent/loans/disbursed' },
      { title: 'Rejected', path: '/agent/loans/rejected' },
    ],
  },
  // {
  //   title: 'Documents',
  //   icon: Upload,
  //   submenu: [
  //     { title: 'Upload Documents', path: '/agent/documents/upload' },
  //     { title: 'Pending KYC', path: '/agent/documents/kyc' },
  //     { title: 'Rejected', path: '/agent/documents/rejected' },
  //     { title: 'History', path: '/agent/documents/history' },
  //   ],
  // },
  {
    title: 'Follow-Ups',
    icon: Calendar,
    submenu: [
      { title: "Today's", path: '/agent/followups/today' },
      { title: 'Upcoming', path: '/agent/followups/upcoming' },
      { title: 'Overdue', path: '/agent/followups/overdue' },
      { title: 'Completed', path: '/agent/followups/completed' },

    ],
  },
  {
    title: 'Earnings & Commission',
    icon: Wallet,
    submenu: [
      { title: 'This Month', path: '/agent/earnings' },
      { title: 'Pending', path: '/agent/earnings/pending' },
      { title: 'Paid', path: '/agent/earnings/paid' },
      { title: 'History', path: '/agent/earnings/history' },
    ],
  },
  {
    title: 'Payouts',
    icon: CreditCard,
    submenu: [
      { title: 'Payout Status', path: '/agent/payouts/status' },
      { title: 'History', path: '/agent/payouts/history' },
    ],
  },
  // {
  //   title: 'Notifications',
  //   icon: Bell,
  //   submenu: [
  //     { title: 'All Notifications', path: '/agent/notifications' },
  //     { title: 'New Lead', path: '/agent/notifications/new-lead' },
  //     { title: 'Approval Update', path: '/agent/notifications/approval' },
  //     { title: 'Payout Update', path: '/agent/notifications/payout' },
  //   ],
  // },
  // {
  //   title: 'Profile',
  //   icon: User,
  //   submenu: [
  //     { title: 'My Profile', path: '/agent/profile' },
  //     { title: 'Bank Details', path: '/agent/profile/bank' },
  //     { title: 'Support', path: '/agent/profile/support' },
  //   ],
  // },
];

// White Label Admin sidebar menu
const whitelabelMenu = [
  {
    title: 'Dashboard',
    icon: LayoutDashboard,
    path: '/whitelabel/dashboard',

  },
  {
    title: 'Leads Management',
    icon: Users,
    submenu: [
      { title: 'All Leads', path: '/whitelabel/leads' },
      // { title: 'Follow-Up Leads', path: '/whitelabel/leads/followup' },
      { title: 'Converted Leads', path: '/whitelabel/leads/converted' },
      { title: 'Pending Leads', path: '/whitelabel/leads/pending' },
      { title: 'Rejected Leads', path: '/whitelabel/leads/rejected' },
    ],
  },
  {
    title: 'Follow-Ups',
    icon: Calendar,
    submenu: [
      { title: "Today's", path: '/whitelabel/followups/today' },
      { title: 'Upcoming', path: '/whitelabel/followups/upcoming' },
      { title: 'Overdue', path: '/whitelabel/followups/overdue' },
      { title: 'Completed', path: '/whitelabel/followups/completed' },
    ],
  },
  {
    title: 'Loan / File Login',
    icon: FileText,
    submenu: [
      { title: 'All Files', path: '/whitelabel/loans' },
      { title: 'Under Processing', path: '/whitelabel/loans/processing' },
      { title: 'Approved', path: '/whitelabel/loans/approved' },
      { title: 'Disbursed', path: '/whitelabel/loans/disbursed' },
      { title: 'Rejected', path: '/whitelabel/loans/rejected' },
      { title: 'Hold Cases', path: '/whitelabel/loans/hold' },
    ],
  },
  {
    title: 'Agents Management',
    icon: UserCheck,
    submenu: [
      { title: 'All Agents', path: '/whitelabel/agents' },
      // { title: 'Performance', path: '/whitelabel/agents/performance' },
      // { title: 'Target Setup', path: '/whitelabel/agents/targets' },
    ],
  },
  {
    title: 'Employees Management',
    icon: Briefcase,
    submenu: [
      { title: 'All Employees', path: '/whitelabel/employees' },
      { title: 'Role & Permission', path: '/whitelabel/employees/roles' },
      // { title: 'Activity Logs', path: '/whitelabel/employees/logs' },
    ],
  },
  // {
  //   title: 'Upgrade Plan',
  //   icon: CreditCard,
  //   path: '/whitelabel/upgrade-plan',
  // },
  {
    title: 'Commission',
    icon: DollarSign,
    requiredFeature: 'commission_module',
    submenu: [
      { title: 'Commission Packages', path: '/whitelabel/commission/slabs' },
      { title: 'Pending Payouts', path: '/whitelabel/commission/pending' },
      { title: 'Approved Payouts', path: '/whitelabel/commission/approved' },
      { title: 'Commission Ledger', path: '/whitelabel/commission/ledger' },
    ],
  },
  {
    title: 'Banks / NBFC',
    icon: Building2,
    requiredFeature: 'bank_integration',
    submenu: [
      { title: 'Loan Schemes', path: '/whitelabel/banks' },
      { title: 'Interest Setup', path: '/whitelabel/banks/interest' },
      { title: 'Processing Fees', path: '/whitelabel/banks/fees' },
    ],
  },
  {
    title: 'Reports',
    icon: BarChart3,
    requiredFeature: 'reports',
    submenu: [
      { title: 'Revenue Report', path: '/whitelabel/reports/revenue' },
      { title: 'Conversion Report', path: '/whitelabel/reports/conversion' },
      { title: 'Agent-wise Report', path: '/whitelabel/reports/agents' },
      // { title: 'Export Data', path: '/whitelabel/reports/export' },
    ],
  },
  // {
  //   title: 'Alerts',
  //   icon: Bell,
  //   submenu: [
  //     { title: 'Pending Approvals', path: '/whitelabel/alerts/pending' },
  //     { title: 'Overdue Files', path: '/whitelabel/alerts/overdue' },
  //     { title: 'High Value Alerts', path: '/whitelabel/alerts/high-value' },
  //   ],
  // },
  {
    title: 'Branding Settings',
    icon: Palette,
    requiredFeature: 'white_label',
    submenu: [
      { title: 'Logo & Colors', path: '/whitelabel/branding/logo' },
      { title: 'Portal Name', path: '/whitelabel/branding/name' },
      { title: 'Custom Domain', path: '/whitelabel/branding/domain' },
    ],
  },
  {
    title: 'Profile & Settings',
    icon: User,
    submenu: [
      { title: 'My Profile', path: '/whitelabel/profile' },
      { title: 'Change Password', path: '/whitelabel/profile/password' },
      // { title: 'Security Settings', path: '/whitelabel/profile/security' },
    ],
  },
];

const filterWhiteLabelMenu = (workspace = null) => {
  const features = workspace?.feature_permissions || {};
  return whitelabelMenu.filter((item) => {
    if (!item.requiredFeature) return true;
    if (item.requiredFeature === 'reports') return Boolean(features.reports);
    return Boolean(features[item.requiredFeature]);
  });
};

const getMenuForRole = (role, permissions = [], workspace = null) => {
  switch (role) {
    case 'super_admin':
      return adminMenu;
    case 'wl_admin':
      return filterWhiteLabelMenu(workspace);
    case 'employee':
      // Filter based on the employee's actual role permissions
      return filterEmployeeMenu(permissions);
    case 'agent':
      return agentMenu;
    default:
      return [];
  }
};

const MenuItem = ({ item, isCollapsed }) => {
  const [isOpen, setIsOpen] = useState(false);
  const location = useLocation();
  const isActive = item.submenu?.some((sub) => location.pathname === sub.path);
  const Icon = item.icon;

  // 👉 Direct path item (like Dashboard)
  if (!item.submenu && item.path) {
    return (
      <Link to={item.path}>
        <Button
          variant="ghost"
          className={cn(
            isCollapsed ? 'w-full justify-center p-2' : 'w-full justify-between p-3 h-auto',
            location.pathname === item.path && 'bg-accent/50'
          )}
        >
          <div className={cn('flex items-center', isCollapsed ? 'justify-center' : 'gap-3')}>
            <Icon className="h-5 w-5" />
            {!isCollapsed && (
              <span className="text-sm font-medium">{item.title}</span>
            )}
          </div>
        </Button>
      </Link>
    );
  }
  if (isCollapsed) {
    return (
      <div className="relative group">
        <Button
          variant="ghost"
          className={cn(
            'w-full justify-center p-2',
            isActive && 'bg-accent text-accent-foreground '
          )}
        >
          <Icon className="h-5 w-5" />
        </Button>
        <div className="absolute left-full top-0 ml-2 hidden group-hover:block z-50">
          <div className="bg-popover border rounded-lg shadow-lg p-2 min-w-[200px]">
            <p className="font-semibold text-sm mb-2 px-2">{item.title}</p>
            {item.submenu?.map((sub) => (
              <Link
                key={sub.path}
                to={sub.path}
                className={cn(
                  'block px-3 py-2 rounded-md text-sm hover:bg-accent transition-colors',
                  location.pathname === sub.path && 'bg-accent text-accent-foreground'
                )}
              >
                {sub.title}
              </Link>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="mt-2">
      <Button
        variant="ghost"
        onClick={() => setIsOpen(!isOpen)}
        className={cn(
          'w-full justify-between p-3 h-auto',
          isActive && 'bg-accent/50'
        )}
        data-testid={`sidebar-menu-${item.title.toLowerCase().replace(/\s+/g, '-')}`}
      >
        <div className="flex items-center gap-3">
          <Icon className="h-5 w-5" />
          <span className="text-sm font-medium">{item.title}</span>
        </div>
        {isOpen ? (
          <ChevronDown className="h-4 w-4" />
        ) : (
          <ChevronRight className="h-4 w-4" />
        )}
      </Button>
      {isOpen && (
        <div className="ml-4 mt-1 space-y-1 border-l pl-4">
          {item.submenu?.map((sub) => (
            <Link
              key={sub.path}
              to={sub.path}
              className={cn(
                'block px-3 py-2 rounded-md text-sm hover:bg-accent transition-colors py-2 mt-1',
                location.pathname === sub.path &&
                'bg-accent text-accent-foreground font-medium'
              )}
              data-testid={`sidebar-link-${sub.title.toLowerCase().replace(/\s+/g, '-')}`}
            >
              {sub.title}
            </Link>
          ))}
        </div>
      )}
    </div>
  );
};

export const Sidebar = ({ isCollapsed, onToggle, hasTopBanner = false }) => {
  const { user } = useAuth();
  const menu = getMenuForRole(user?.role, user?.permissions, user?.workspace);

  return (
    <>
      <aside
        className={cn(
          'fixed left-0 h-full bg-background border-r border-border transition-all duration-300 z-50 md:z-40 shadow-xl md:shadow-none',
          hasTopBanner ? 'top-12 md:top-28 md:h-[calc(100vh-7rem)]' : 'top-0 md:top-16 md:h-[calc(100vh-4rem)]',
          isCollapsed ? '-translate-x-full md:translate-x-0 md:w-16' : 'translate-x-0 w-[280px] md:w-64'
        )}
        data-testid="sidebar"
      >
        <div className="flex items-center justify-between p-4 md:hidden border-b border-border">
          <span className="font-bold text-lg text-emerald-600">Menu</span>
          <Button variant="ghost" size="icon" onClick={onToggle}>
            <X className="h-6 w-6" />
          </Button>
        </div>

        <ScrollArea className="h-full py-4">
          <div className={cn('space-y-2', isCollapsed ? 'px-2' : 'px-3')}>
            {menu.map((item) => (
              <MenuItem key={item.title} item={item} isCollapsed={isCollapsed} />
            ))}
          </div>
        </ScrollArea>
      </aside>
    </>
  );
};

export default Sidebar;
